import React, { useEffect } from 'react';
import CompanyTags from '@componentsV2/CompanyProfile/CompanyTags';
import { Card } from '@/components/ui/card';
import CompanyLogoBox from '@componentsV2/CompanyProfile/CompanyLogoBox';
import Link from '@/components/atomic/Link';
import { ExternalLinkIcon, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import clsx from 'clsx';
import { isURL } from '@/utils/companies';
import ImageGallery from '@/components/ImageGallery';
import matomo from '@/utils/matomo';
// import CompanyRecommendationCard from '@componentsV2/CompanyProfile/CompanyRecommendationCard'; //Todo: concept not implemented yet

const CompanyProfile = ({ company }) => {
  const isPremium = company.hasProfileSearchAddon;
  const isNotEmptyArr = (arr) => arr?.length > 0;

  useEffect(() => {
    matomo.clickEvent({
      category: 'Company profile Page',
      name: `Company profile page view with id ${company.id} and name ${company.name}`,
      action: 'Company profile page view',
    });
  }, []);

  return (
    <div>
      <h2 className="text-3xl font-bold text-foreground mb-[18px]">
        {company.displayName}
      </h2>
      <div className="mb-[18px]">
        <CompanyTags
          company={company}
          variant={isPremium ? 'default' : 'white'}
        />
      </div>
      <div className="flex flex-col-reverse desktop:flex-row w-full gap-[20px] items-start">
        <div
          className={`flex flex-col w-full desktop:max-w-[680px] ${
            isPremium ? 'gap-[13px]' : 'gap-[20px]'
          }`}
        >
          <Card className="flex flex-col p-3 sm:p-6 gap-3 sm:gap-6">
            <h3 className="font-semibold text-xl sm:text-2xl">Om selskapet</h3>
            <p className="text-xs sm:text-sm text-foreground">
              {company.companyDescription || (
                <div>
                  <p className="text-xs sm:text-sm text-foreground">
                    {company.displayName}{' '}
                    {company.employeeCount &&
                      `har ${company.employeeCount} ansatte og`}{' '}
                    hører til i {company.address.postalPlace}.
                  </p>
                  {company.industryAssociations.length > 0 && (
                    <>
                      De er registrert i følgende bransje:
                      <ul>
                        {company.industryAssociations.map((el) => (
                          <li
                            key={el.id}
                            className="list-disc ml-5 text-xs sm:text-sm text-foreground"
                          >
                            {el.name}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </p>
          </Card>
          {isNotEmptyArr(company.franchiseCompany) && (
            <Card className="flex flex-col p-3 sm:p-6 gap-3 sm:gap-6">
              <h3 className="font-semibold text-xl sm:text-2xl">
                Kjedetilhørighet
              </h3>
              <div
                className={clsx([
                  'flex flex-wrap',
                  isPremium ? 'gap-[30px]' : 'flex-col',
                ])}
              >
                {isPremium &&
                  company.franchiseCompany.map((frCompany) =>
                    frCompany.franchiserLogoPath ? (
                      <CompanyLogoBox url={frCompany.franchiserLogoPath} />
                    ) : (
                      <p className="font-semibold text-sm sm:text-base">
                        {frCompany.displayName}
                      </p>
                    )
                  )}
                {!isPremium &&
                  company.franchiseCompany.map((frCompany) => (
                    <p className="font-semibold text-sm sm:text-base">
                      {frCompany.displayName}
                    </p>
                  ))}
              </div>
            </Card>
          )}
          {isNotEmptyArr(company.companyCertificates) && (
            <Card className="flex flex-col p-3 sm:p-6 gap-3 sm:gap-6">
              <h3 className="font-semibold text-xl sm:text-2xl">
                Sertifiseringer
              </h3>
              <div className="flex gap-[20px]">
                {isPremium &&
                  company.companyCertificates.map((certificate) => (
                    <img
                      className="w-[72px] h-[72px] object-contain"
                      alt="certificate"
                      src={certificate.logoPath}
                    />
                  ))}
                {!isPremium && (
                  <div>
                    {company.companyCertificates.map((certificate) => (
                      <p className="font-semibold text-sm sm:text-base">
                        {certificate.name}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}

          {isPremium && isNotEmptyArr(company.companyProfileImagePath) && (
            <Card className="flex flex-col p-3 sm:p-6 gap-3 sm:gap-6 overflow-hidden">
              <h3 className="font-semibold text-xl sm:text-2xl">
                Bildegalleri
              </h3>
              <div
                className={`flex gap-[20px] ${
                  company.companyProfileImagePath.length > 1 && 'mr-[-40px]'
                } overflow-hidden`}
              >
                <ImageGallery
                  slideGap
                  imageContain
                  showIndex={false}
                  onlyFullScreenArrows
                  dots={false}
                  slidesToShow={
                    company.companyProfileImagePath.length > 3
                      ? 3
                      : company.companyProfileImagePath.length
                  }
                  className="w-full"
                  images={company.companyProfileImagePath.map((el) => ({
                    url: el,
                  }))}
                />
              </div>
            </Card>
          )}
        </div>
        <Card className="w-full desktop:max-w-[360px]">
          {isPremium && (
            <div className="flex justify-between p-3 sm:p-4">
              <img
                className="max-w-[calc(100%-40px)] max-h-[120px] object-contain"
                alt="company logo"
                src={company.companyLogo}
              />
              {isURL(company.website) && (
                <Link
                  className="flex items-center justify-center w-10 h-10"
                  to={company.website}
                  isExternal
                  openInNewTab
                >
                  <ExternalLinkIcon width={12} />
                </Link>
              )}
            </div>
          )}
          <div className="flex flex-col border-t-[1px] p-3 sm:p-4 gap-3 sm:gap-4">
            <h3 className="font-semibold text-xl sm:text-2xl">Informasjon</h3>
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Juridisk navn
              </p>
              <h4 className="text-sm sm:text-base font-semibold">
                {company.name}
              </h4>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Organisasjonsnummer
              </p>
              <h4 className="text-sm sm:text-base font-semibold">
                {new Intl.NumberFormat('fr-FR').format(
                  company.organizationNumber
                )}
              </h4>
            </div>
            {isPremium && (
              <>
                {!!company.jobsCount && (
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Dokumenterte jobber
                    </p>
                    <h4 className="text-sm sm:text-base font-semibold">
                      {company.jobsCount}
                    </h4>
                  </div>
                )}
                {!!company.employeeCount && (
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Ansatte
                    </p>
                    <h4 className="text-sm sm:text-base font-semibold">
                      {company.employeeCount}
                    </h4>
                  </div>
                )}
                {!!company.performsJobsIn && (
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Utfører jobber i
                    </p>
                    <h4 className="text-sm sm:text-base font-semibold">
                      {company.performsJobsIn}
                    </h4>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex items-center justify-center border-t-[2px] p-3 sm:p-4">
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant={isPremium ? 'default' : 'secondary'}
                  className={clsx([!isPremium && 'border'])}
                  disabled={!isPremium || (!company.phone && !company.email)}
                  onClick={() =>
                    matomo.clickEvent({
                      category: 'Company profile Page',
                      name: `Company profile contact button click with id ${company.id} and name ${company.name}`,
                      action: 'Company profile contact button click',
                    })
                  }
                >
                  <Phone /> Vis kontaktinformasjon
                </Button>
              </DialogTrigger>
              <DialogContent className="flex flex-col items-start">
                <DialogTitle>
                  <h3 className="text-lg font-semibold">Kontaktinformasjon</h3>
                </DialogTitle>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Adresse
                  </p>
                  <h4 className="text-sm sm:text-base font-semibold">
                    {company.address.streetName || ''}&nbsp;
                    {company.address.houseNumber || ''}&nbsp;
                    {company.address.houseSubNumber || ''}
                  </h4>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Sted
                  </p>
                  <h4 className="text-sm sm:text-base font-semibold">
                    {company.address.postalCode || ''}&nbsp;
                    {company.address.postalPlace || ''}
                  </h4>
                </div>
                {company.phone && (
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Tlf nr
                    </p>
                    <h4 className="text-sm sm:text-base font-semibold">
                      {company.phone
                        .toString()
                        .replace(/(\d{2})(?=\d)/g, '$1 ')}
                    </h4>
                  </div>
                )}
                {company.email && (
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Epost
                    </p>
                    <h4 className="text-sm sm:text-base font-semibold">
                      {company.email}
                    </h4>
                  </div>
                )}
                <DialogTrigger asChild>
                  <Button variant="secondary">Close</Button>
                </DialogTrigger>
              </DialogContent>
            </Dialog>
          </div>
        </Card>
      </div>
      {/* {!isPremium && isNotEmptyArr(recommendations) && ( */}
      {/*   <Card className="flex flex-col p-3 sm:p-6 gap-3 sm:gap-6 mt-[15px] items-center desktop:items-start"> */}
      {/*     <h3 className="text-center sm:text-left font-semibold text-xl sm:text-2xl"> */}
      {/*       Lignende bedrifter i nærheten */}
      {/*     </h3> */}
      {/*     <div className="flex flex-wrap gap-[22px] justify-center desktop:justify-start"> */}
      {/*       {recommendations.map((el) => ( */}
      {/*         <CompanyRecommendationCard company={el} /> //Todo: concept not implemented yet */}
      {/*       ))} */}
      {/*     </div> */}
      {/*   </Card> */}
      {/* )} */}
    </div>
  );
};

export default CompanyProfile;
